import { isEqual } from 'lodash'
import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { FileRecord, useFileUploadContext } from '../controller/UploadFileContext'
import { UploadFileFormat } from '../types'

export const useUpdateExternalWithFileChanges = (
  slot: string,
  multiple: boolean,
  formFormat: UploadFileFormat,
  previousValueRef: MutableRefObject<any>,
  onChange?: (value: any) => void,
  onChangePreview?: (value: any) => void
) => {
  const { fileRecords } = useFileUploadContext()
  const previousRealValueRef = useRef<any>(null)
  const previousPreviewValueRef = useRef<any>(null)
  const isInternalUpdateRef = useRef(false)
  const [firstRender, setFirstRender] = useState(true)

  useEffect(() => {
    previousRealValueRef.current = previousValueRef.current
    previousPreviewValueRef.current = previousValueRef.current
  }, [previousValueRef.current])

  // Watch for fileRecords changes in context and update form field accordingly
  useEffect(() => {
    if (firstRender) {
      // Always skip this on the first render, otherwise, this can de-populate the parent form
      setFirstRender(false)
      return
    }
    // Helper function to convert a FileRecord to the target form format
    const convertRecordToFormValue = (record: FileRecord): any => {
      if (record.status === 'existing' && record.existingValue) {
        // Use the original existingValue format
        return record.existingValue
      } else if (record.status === 'uploaded' || record.status === 'existing') {
        if (formFormat === 'record') {
          return record.fileRecord
        } else if (formFormat === 'record_url') {
          return record.fileRecord?.url
        } else if (formFormat === 'public_url') {
          return record.fileRecord?.file_contents || record.url
        } else if (record.id) {
          // Output {id: number} format (default)
          return { id: record.id }
        }
      } else if (record.status === 'pending') {
        // For pending records, create temporary representation
        if (formFormat === 'record') {
          // Create a temporary record with no ID but with the temporary local URL
          return {
            ...(record.fileRecord || {}),
            id: undefined,
            file_contents: record.url || undefined,
            title: record.name,
          }
        } else if (formFormat === 'record_url') {
          return record.url || null
        } else if (formFormat === 'public_url') {
          return record.url || null
        }
        // For 'id' format, pending records don't have an ID yet, so return null
        return null
      }
      return null
    }

    // Helper function to convert array of records to form value
    const convertRecordsToFormValue = (records: FileRecord[]): any => {
      if (records.length === 0) {
        return multiple ? [] : null
      }

      const formValues = records
        .map(convertRecordToFormValue)
        .filter((v): v is { id: number } | string | any => v !== null)

      return multiple ? (formValues.length > 0 ? formValues : []) : formValues.length > 0 ? formValues[0] : null
    }
    const currentRecords = fileRecords.filter((r) => r.slot === slot)

    // Convert all records (of all statuses) to the target representation
    const allFormValues = convertRecordsToFormValue(currentRecords)

    // Build array with only existing+uploaded records
    const existingAndUploadedRecords = currentRecords.filter((r) => r.status === 'existing' || r.status === 'uploaded')
    const existingAndUploadedFormValues = convertRecordsToFormValue(existingAndUploadedRecords)

    // Compare preview values (all records) and call onChangePreview if changed
    if (onChangePreview && !isEqual(previousPreviewValueRef.current, allFormValues)) {
      isInternalUpdateRef.current = true
      onChangePreview(allFormValues)
      previousPreviewValueRef.current = allFormValues
      Promise.resolve().then(() => {
        isInternalUpdateRef.current = false
      })
    }

    // Compare existing+uploaded values and call onChange if changed
    if (onChange && !isEqual(previousRealValueRef.current, existingAndUploadedFormValues)) {
      isInternalUpdateRef.current = true
      onChange(existingAndUploadedFormValues)
      previousRealValueRef.current = existingAndUploadedFormValues
      Promise.resolve().then(() => {
        isInternalUpdateRef.current = false
      })
    }
  }, [fileRecords, slot, multiple, formFormat, onChange, onChangePreview])

  return { isInternalUpdateRef }
}
